import { record } from "rrweb";
import type { eventWithTime } from "@rrweb/types";

export interface SiteAnalyticConfig {
  /** Base URL of the ingest backend, e.g. "https://analytics.mysite.com" */
  endpoint: string;
  /** Public site identifier, issued per site/project */
  siteId: string;
  /** How many events to buffer before flushing (default 20) */
  batchSize?: number;
  /** Max ms to wait before flushing a non-empty buffer (default 5000) */
  flushIntervalMs?: number;
}

function randomId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** "/checkout" matches only that exact path; "/checkout/*" matches it and anything under it. */
function pathIsExcluded(pathname: string, patterns: string[]): boolean {
  return patterns.some((pattern) =>
    pattern.endsWith("*") ? pathname.startsWith(pattern.slice(0, -1)) : pathname === pattern
  );
}

async function fetchExcludedPaths(config: SiteAnalyticConfig): Promise<string[]> {
  try {
    const res = await fetch(`${config.endpoint}/api/track-config/${encodeURIComponent(config.siteId)}`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.excludedPaths) ? data.excludedPaths : [];
  } catch {
    // Fail open — a config-fetch hiccup shouldn't silently stop recording everywhere.
    return [];
  }
}

/** Cap on backoff so a misconfigured site still gets retried occasionally. */
const MAX_BACKOFF_MS = 60_000;

class Tracker {
  private buffer: eventWithTime[] = [];
  private sessionId = randomId();
  private stopFn: (() => void) | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private consecutiveFailures = 0;
  private cooldownUntil = 0;

  constructor(private config: SiteAnalyticConfig) {}

  start(): void {
    if (this.stopFn) return;

    this.stopFn =
      record({
        emit: (event) => {
          this.buffer.push(event);
          if (this.buffer.length >= (this.config.batchSize ?? 20)) {
            this.flush();
          }
        },
        // Lets same-origin iframes get recorded automatically, and lets
        // cross-origin iframes relay their events here too — but only if
        // that iframe's own page also calls initChildFrame() below.
        recordCrossOriginIframes: true,
      }) ?? null;

    this.timer = setInterval(
      () => this.flush(),
      this.config.flushIntervalMs ?? 5000,
    );

    window.addEventListener("beforeunload", () => this.flush(true));
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") this.flush(true);
    });
  }

  stop(): void {
    this.stopFn?.();
    this.stopFn = null;
    if (this.timer) clearInterval(this.timer);
    this.flush(true);
  }

  private flush(useBeacon = false): void {
    if (this.buffer.length === 0) return;
    // A persistently rejecting endpoint (e.g. an unregistered origin — a 403
    // that will never turn into a 2xx) shouldn't be hammered every batch/tick;
    // back off instead of firing a fresh request each time. Unload-time
    // beacons bypass this since there's only ever one shot at those.
    if (!useBeacon && Date.now() < this.cooldownUntil) return;

    const events = this.buffer;
    this.buffer = [];

    const payload = JSON.stringify({
      siteId: this.config.siteId,
      sessionId: this.sessionId,
      events,
    });

    const url = `${this.config.endpoint}/api/events`;

    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(
        url,
        new Blob([payload], { type: "application/json" }),
      );
      return;
    }

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      // No `keepalive` here: the page is still alive for this path (unload
      // uses sendBeacon above), and keepalive requests are capped at 64 KiB —
      // the initial FullSnapshot batch routinely exceeds that and would
      // silently fail to send, leaving every session without a starting frame.
    })
      .then((res) => {
        if (res.ok) {
          this.consecutiveFailures = 0;
          this.cooldownUntil = 0;
        } else {
          this.registerFailure();
        }
        // Best-effort delivery either way — the batch itself isn't retried.
      })
      .catch(() => {
        this.registerFailure();
      });
  }

  private registerFailure(): void {
    this.consecutiveFailures = Math.min(this.consecutiveFailures + 1, 6);
    this.cooldownUntil =
      Date.now() +
      Math.min(MAX_BACKOFF_MS, 1000 * 2 ** this.consecutiveFailures);
  }
}

let instance: Tracker | null = null;

export function init(config: SiteAnalyticConfig): void {
  if (instance) return;
  const tracker = new Tracker(config);
  instance = tracker;

  fetchExcludedPaths(config).then((excludedPaths) => {
    // Bail if something else already stopped/reset the tracker while the
    // config fetch was in flight (e.g. shutdown() called immediately after init()).
    if (instance !== tracker) return;
    if (pathIsExcluded(window.location.pathname, excludedPaths)) {
      instance = null;
      return;
    }
    tracker.start();
  });
}

export function shutdown(): void {
  instance?.stop();
  instance = null;
}

let childFrameStop: (() => void) | null = null;

/**
 * Call this instead of `init()` on a page that's embedded as a *cross-origin*
 * iframe inside a page you also control and are already tracking with `init()`.
 * It doesn't talk to the backend itself — it only relays rrweb events up to
 * `window.parent` (rrweb's own postMessage relay, gated on
 * `recordCrossOriginIframes`), and the parent page's tracker stitches them
 * into its own session. Same-origin iframes don't need this — the parent
 * recorder can already see into those directly.
 */
export function initChildFrame(): void {
  if (childFrameStop || window.parent === window) return;
  childFrameStop = record({ recordCrossOriginIframes: true }) ?? null;
}

export function stopChildFrame(): void {
  childFrameStop?.();
  childFrameStop = null;
}
